import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { Loaded } from "@/components/Loading";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { usePeople } from "@/lib/hooks/usePeople";
import { call, CALLABLES } from "@/services/callables";
import { formatNumber, formatPercent } from "@/lib/format";
import { fromDateInput, rangeFor, toDateInput, type RangePreset } from "./ranges";
import { downloadCsv, exportFilename, toCsv, type ExportColumn } from "./export";
import type {
  ActivityReport,
  ContentReport,
  DateRange,
  InstitutionReport,
  TeacherActivityReport,
} from "@/types";

/**
 * Reports.
 *
 * **Built from statistics only.** Never from the event log, never from a run,
 * never from a score — the statistics collection exists precisely so that a
 * number an owner may see can be produced without a query that could have
 * returned a name. The note at the top says so, because an institution's data
 * protection officer will ask and should not have to take it on trust.
 *
 * A teacher sees which scenes worked, and nothing about their colleagues.
 */

const PRESETS: readonly { id: RangePreset; labelKey: string }[] = [
  { id: "month", labelKey: "reports.range.month" },
  { id: "term", labelKey: "reports.range.term" },
  { id: "year", labelKey: "reports.range.year" },
  { id: "custom", labelKey: "reports.range.custom" },
];

export function ReportsPage() {
  const t = useT();
  const { user, institution } = useAuth();
  const [preset, setPreset] = useState<RangePreset>("term");
  const [custom, setCustom] = useState<DateRange>(() => rangeFor("month", new Date()));

  const range = useMemo(
    () => rangeFor(preset, new Date(), custom),
    [preset, custom]
  );

  const isOwner = user?.role === "owner";

  return (
    <SectionShell titleKey="reports.title">
      <p className="max-w-2xl text-sm text-ink-muted">{t("reports.noStudentData")}</p>
      {!isOwner ? (
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t("reports.teacherScope")}</p>
      ) : null}

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1">
          {PRESETS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={entry.id === preset}
              onClick={() => setPreset(entry.id)}
              className={`chip ${
                entry.id === preset
                  ? "border-brand bg-brand-soft font-medium text-brand-ink"
                  : "border-line bg-white text-ink-soft hover:border-line-strong hover:text-ink"
              }`}
            >
              {t(entry.labelKey)}
            </button>
          ))}
        </div>

        {preset === "custom" ? (
          <>
            <label>
              <span className="field-label">{t("reports.range.from")}</span>
              <input
                type="date"
                className="field-input"
                value={toDateInput(custom.from)}
                onChange={(event) =>
                  setCustom({ ...custom, from: fromDateInput(event.target.value) })
                }
              />
            </label>
            <label>
              <span className="field-label">{t("reports.range.to")}</span>
              <input
                type="date"
                className="field-input"
                value={toDateInput(custom.to)}
                onChange={(event) =>
                  setCustom({ ...custom, to: fromDateInput(event.target.value) })
                }
              />
            </label>
          </>
        ) : null}
      </div>

      <div className="mt-10 flex flex-col gap-12">
        {isOwner ? <Usage range={range} /> : null}
        <Content range={range} institutionName={institution?.name ?? ""} />
        {isOwner ? <Activity range={range} institutionName={institution?.name ?? ""} /> : null}
        {isOwner ? <Teachers range={range} institutionName={institution?.name ?? ""} /> : null}
        {isOwner ? <Summary range={range} /> : null}
      </div>
    </SectionShell>
  );
}

function Usage({ range }: { range: DateRange }) {
  const t = useT();
  const { locale } = useI18n();
  const report = useCallable<InstitutionReport, DateRange>(
    CALLABLES.getInstitutionReport,
    range
  );
  const billing = useCallable<{ teacherSeats: number; teacherSeatsUsed: number }>(
    CALLABLES.getBillingStatus
  );

  return (
    <section>
      <h2 className="section-title">{t("reports.usage.title")}</h2>
      <Loaded state={report}>
        {(data) => (
          <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-3 xl:grid-cols-4">
            <Figure labelKey="reports.usage.sessions" value={formatNumber(locale, data.sessionCount)} />
            <Figure labelKey="reports.usage.runs" value={formatNumber(locale, data.runCount)} />
            <Figure labelKey="reports.usage.groups" value={formatNumber(locale, data.activeGroups)} />
            <Figure labelKey="reports.usage.teachers" value={formatNumber(locale, data.activeTeachers)} />
            <Figure labelKey="reports.usage.minutes" value={formatNumber(locale, data.totalMinutes)} />
            <Figure
              labelKey="reports.usage.attendance"
              value={formatNumber(locale, Math.round(data.averageAttendance * 10) / 10)}
            />
            <Figure
              labelKey="reports.usage.attendanceRate"
              value={formatPercent(locale, data.averageAttendanceRate)}
            />
            <Figure
              labelKey="reports.usage.seats"
              value={
                billing.data
                  ? t("reports.usage.seatsOf", {
                      used: billing.data.teacherSeatsUsed,
                      total: billing.data.teacherSeats,
                    })
                  : "—"
              }
            />
          </dl>
        )}
      </Loaded>
    </section>
  );
}

function Figure({ labelKey, value }: { labelKey: string; value: string }) {
  const t = useT();
  return (
    <div>
      <dt className="text-sm text-ink-muted">{t(labelKey)}</dt>
      <dd className="mt-1 text-[24px] font-semibold tabular-nums tracking-[-0.01em] text-ink">{value}</dd>
    </div>
  );
}

const CONTENT_COLUMNS: readonly ExportColumn[] = [
  /*
    The name, not the id.

    This column printed `LXFdqZwU6kXzyfSQMpv4` under a heading that said
    "Scene", to an owner, on a page meant for a meeting — the same fault the
    teacher's debrief had with student uids. A scene deleted since falls back to
    its id rather than to a blank row.
  */
  { key: "scene", headerKey: "reports.content.column.scenario" },
  { key: "uses", headerKey: "reports.content.column.uses" },
  { key: "repeatRate", headerKey: "reports.content.column.repeatRate" },
  { key: "groupsUsedIn", headerKey: "reports.content.column.groups" },
  { key: "teachersUsedBy", headerKey: "reports.content.column.teachers" },
  { key: "averageSessionMinutes", headerKey: "reports.content.column.minutes" },
];

function Content({ range, institutionName }: { range: DateRange; institutionName: string }) {
  const t = useT();
  const report = useCallable<ContentReport, DateRange>(CALLABLES.getContentReport, range);

  return (
    <section>
      <h2 className="section-title">{t("reports.content.title")}</h2>
      <p className="mt-1 max-w-2xl text-sm text-ink-muted">{t("reports.content.intro")}</p>

      <Loaded state={report}>
        {(data) => (
          <ReportTable
            columns={CONTENT_COLUMNS}
            rows={
              data.scenarios.map((row) => ({
                ...row,
                // Live runs, room lessons and show nights have no scene and
                // no title; each is one row named for what it was.
                scene:
                  row.kind === "room" || row.kind === "show"
                    ? t(`sessions.kind.${row.kind}`)
                    : row.live
                      ? t("review.liveScene")
                      : row.title || row.scenarioId,
              })) as unknown as Record<string, unknown>[]
            }
            rowKey={(row) => String(row.scenarioId)}
            captionKey="reports.content.title"
            filename={exportFilename(institutionName, "content", range)}
            range={range}
          />
        )}
      </Loaded>
    </section>
  );
}

const ACTIVITY_COLUMNS: readonly ExportColumn[] = [
  { key: "month", headerKey: "reports.activity.column.month" },
  { key: "sessionCount", headerKey: "reports.activity.column.sessions" },
  { key: "runCount", headerKey: "reports.activity.column.runs" },
  { key: "activeGroups", headerKey: "reports.activity.column.groups" },
  { key: "totalMinutes", headerKey: "reports.activity.column.minutes" },
];

function Activity({ range, institutionName }: { range: DateRange; institutionName: string }) {
  const t = useT();
  const report = useCallable<ActivityReport, DateRange>(CALLABLES.getActivityReport, range);

  return (
    <section>
      <h2 className="section-title">{t("reports.activity.title")}</h2>
      <Loaded state={report}>
        {(data) => (
          <ReportTable
            columns={ACTIVITY_COLUMNS}
            rows={data.months as unknown as Record<string, unknown>[]}
            rowKey={(row) => String(row.month)}
            captionKey="reports.activity.title"
            filename={exportFilename(institutionName, "activity", range)}
            range={range}
          />
        )}
      </Loaded>
    </section>
  );
}

const TEACHER_COLUMNS: readonly ExportColumn[] = [
  { key: "teacherName", headerKey: "reports.teachers.column.teacher" },
  { key: "sessionCount", headerKey: "reports.teachers.column.sessions" },
  { key: "runCount", headerKey: "reports.teachers.column.runs" },
  { key: "groupsTaught", headerKey: "reports.teachers.column.groups" },
  { key: "totalMinutes", headerKey: "reports.teachers.column.minutes" },
];

function Teachers({ range, institutionName }: { range: DateRange; institutionName: string }) {
  const t = useT();
  const { people } = usePeople();
  const report = useCallable<TeacherActivityReport, DateRange>(
    CALLABLES.getTeacherActivity,
    range
  );

  const nameOf = (uid: string) =>
    people.find((person) => person.uid === uid)?.displayName ?? uid;

  return (
    <section>
      <h2 className="section-title">{t("reports.teachers.title")}</h2>
      {/*
        Not a ranking, and deliberately not sortable. A league table of
        employees is a performance-monitoring measure with a works council
        attached (§26 BDSG, BetrVG §87(1) Nr. 6), and how much somebody used a
        tool says nothing about how well they taught.
      */}
      <p className="mt-1 max-w-2xl text-sm text-ink-muted">
        {t("reports.teachers.usageNotPerformance")}
      </p>

      <Loaded state={report}>
        {(data) => (
          <ReportTable
            columns={TEACHER_COLUMNS}
            rows={data.teachers.map((row) => ({
              teacherName: nameOf(row.teacherUid),
              sessionCount: row.sessionCount,
              runCount: row.runCount,
              groupsTaught: row.groupsTaught,
              totalMinutes: row.totalMinutes,
            }))}
            rowKey={(row) => String(row.teacherName)}
            captionKey="reports.teachers.title"
            filename={exportFilename(institutionName, "teacher-activity", range)}
            range={range}
          />
        )}
      </Loaded>
    </section>
  );
}

/**
 * A table and the file it becomes.
 *
 * One component for both, so the export cannot drift from the screen: the same
 * column list drives the header row, the cells and the CSV.
 */
function ReportTable({
  columns,
  rows,
  rowKey,
  captionKey,
  filename,
}: {
  columns: readonly ExportColumn[];
  rows: readonly Record<string, unknown>[];
  rowKey: (row: Record<string, unknown>) => string;
  captionKey: string;
  filename: string;
  range: DateRange;
}) {
  const t = useT();
  const { success, showError } = useToast();

  if (rows.length === 0) {
    return <p className="mt-4 text-sm text-ink-muted">{t("reports.empty")}</p>;
  }

  return (
    <>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-[14px]">
          <caption className="sr-only">{t(captionKey)}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
              {columns.map((column) => (
                <th key={column.key} scope="col" className="h-9 px-3 text-[13px] font-medium">
                  {t(column.headerKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className="border-b border-line last:border-0">
                {columns.map((column) => (
                  <td key={column.key} className="h-10 px-3 py-2.5 text-ink-soft">
                    {String(row[column.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button
        variant="secondary"
        className="mt-4"
        onClick={() => {
          try {
            downloadCsv(filename, toCsv(columns, rows, t));
            success("reports.exported");
          } catch (error) {
            // The allow-list throws rather than filtering. A silently dropped
            // column is a wrong export that looks right.
            showError(error);
          }
        }}
      >
        {t("reports.export")}
      </Button>
    </>
  );
}

function Summary({ range }: { range: DateRange }) {
  const t = useT();
  const { showError } = useToast();
  const [text, setText] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  return (
    <section>
      <h2 className="section-title">{t("reports.summary.title")}</h2>

      {text ? (
        <>
          <p className="mt-3 max-w-2xl text-ink-soft">{text}</p>
          {/* Labelled, always. A paragraph nobody wrote should say so. */}
          <p className="mt-2 max-w-2xl text-xs text-ink-faint">{t("reports.summary.label")}</p>
        </>
      ) : null}

      <Button
        variant="secondary"
        busy={busy}
        className="mt-4"
        onClick={async () => {
          setBusy(true);
          try {
            const result = await call<DateRange, { summary: string }>(
              CALLABLES.summariseStatistics,
              range
            );
            setText(result.summary);
          } catch (error) {
            showError(error);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? t("reports.summary.writing") : t("reports.summary.generate")}
      </Button>
    </section>
  );
}
